// Staff PWA shell — header + bottom nav. Three primary destinations:
// Stamp (scanner), Enroll (mint QR for customer), Me (account + logout).
//
// Mounted under /staff/* by StaffApp. Reuses the .owner-root theme tokens
// so colors and typography match the owner app.

import { useState } from 'react'
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'

import { OwnerThemeToggle } from '../owner/components/OwnerThemeToggle.js'
import { useStaffLogout, useStaffSession } from './hooks/useStaffSession.js'

function HomeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 11l8-7 8 7v9a1 1 0 01-1 1h-4v-6h-6v6H5a1 1 0 01-1-1v-9z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  )
}

function StampIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function EnrollIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="4" width="6" height="6" stroke="currentColor" strokeWidth="1.8" />
      <rect x="14" y="4" width="6" height="6" stroke="currentColor" strokeWidth="1.8" />
      <rect x="4" y="14" width="6" height="6" stroke="currentColor" strokeWidth="1.8" />
      <path d="M14 14h3v3M20 14v6M14 20h3" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  )
}

function MeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="3.6" stroke="currentColor" strokeWidth="1.8" />
      <path d="M5 20c0-3.6 3-6 7-6s7 2.4 7 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

// Same 3 destinations as the bottom nav, rendered via .owner-sidebar for
// >=768px viewports (a counter tablet in portrait or landscape). Without
// this, StaffShell had no wide-viewport nav at all — .owner-bottom-nav is
// hidden globally at that breakpoint on the assumption a sidebar takes
// over (see ownerTokens.css), which OwnerShell provides but StaffShell
// didn't, so tablet staff lost all navigation entirely.
function StaffSidebarNav() {
  return (
    <nav className="owner-sidebar" aria-label="Primary">
      <NavLink
        to="/staff"
        end
        className={({ isActive }) =>
          `owner-sidebar-item${isActive ? ' is-active' : ''}`
        }
      >
        <HomeIcon />
        <span>Home</span>
      </NavLink>
      <NavLink
        to="/staff/scan"
        className={({ isActive }) =>
          `owner-sidebar-item${isActive ? ' is-active' : ''}`
        }
      >
        <StampIcon />
        <span>Stamp</span>
      </NavLink>
      <NavLink
        to="/staff/enroll"
        className={({ isActive }) =>
          `owner-sidebar-item${isActive ? ' is-active' : ''}`
        }
      >
        <EnrollIcon />
        <span>Enroll</span>
      </NavLink>
    </nav>
  )
}

export function StaffShell() {
  const navigate = useNavigate()
  const { data: me } = useStaffSession()
  const logout = useStaffLogout()
  const [menuOpen, setMenuOpen] = useState(false)

  function handleLogout() {
    logout.mutate(undefined, {
      onSettled: () => navigate('/staff/login', { replace: true }),
    })
  }

  return (
    <div className="owner-root staff-root">
      <div className="staff-shell">
        <header className="staff-header">
          <Link to="/staff" className="staff-header-brand">
            <span className="staff-header-name">{me?.tenant.name ?? 'Naya Pass'}</span>
            <span className="staff-header-tag">Staff</span>
          </Link>
          <div className="staff-header-right">
            <OwnerThemeToggle />
            <div className="staff-menu-wrap">
              <button
                type="button"
                className="owner-icon-btn"
                aria-label="Account menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((o) => !o)}
              >
                <MeIcon />
              </button>
              {menuOpen && (
                <>
                  <div
                    className="staff-menu-backdrop"
                    onClick={() => setMenuOpen(false)}
                    aria-hidden="true"
                  />
                  <div className="staff-menu-panel" role="menu">
                    <div className="staff-menu-who">
                      {me?.user.display_name ?? 'Staff'}
                    </div>
                    <button
                      type="button"
                      className="owner-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false)
                        handleLogout()
                      }}
                      disabled={logout.isPending}
                    >
                      {logout.isPending ? 'Logging out…' : 'Log out'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <div className="owner-body">
          <StaffSidebarNav />
          <main className="staff-main">
            <Outlet />
          </main>
        </div>

        <nav className="owner-bottom-nav" aria-label="Primary">
          <NavLink
            to="/staff"
            end
            className={({ isActive }) =>
              `owner-bottom-nav-item${isActive ? ' is-active' : ''}`
            }
          >
            <HomeIcon />
            <span>Home</span>
          </NavLink>
          <NavLink
            to="/staff/scan"
            className={({ isActive }) =>
              `owner-bottom-nav-item${isActive ? ' is-active' : ''}`
            }
          >
            <StampIcon />
            <span>Stamp</span>
          </NavLink>
          <NavLink
            to="/staff/enroll"
            className={({ isActive }) =>
              `owner-bottom-nav-item${isActive ? ' is-active' : ''}`
            }
          >
            <EnrollIcon />
            <span>Enroll</span>
          </NavLink>
        </nav>
      </div>
    </div>
  )
}
